/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  ChargingStationList,
  StationManagementInitialProperties,
} from '../../interface/location-management-side-panel.interface';
import stationManagement from '../../utils/api/location-management';

interface Charger {
  id: string;
  chargingPointName: string;
  isPersonalCharger: boolean;
  accountCustomId: string;
  accountName: string;
  chargingStationId: string;
  chargingStationName: string;
  chargingPointSn: string;
}

interface FetchChargingStationListArguments {
  submissionStatus: string;
}
// Async thunk
export const fetchChargingStationList = createAsyncThunk<
  ChargingStationList[],
  FetchChargingStationListArguments
>(
  'location-management/fetchChargingStationList',
  async (data: FetchChargingStationListArguments, { dispatch }) => {
    try {
      const response = await stationManagement.fetchChargingStationList(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

export const fetchCharingPointWithoutPagination = createAsyncThunk<Charger[], any>(
  'location-management/fetchCharingPointWithoutPagination',
  async (data, { dispatch }) => {
    try {
      const response = await stationManagement.fetchChargingPointWithoutPagination(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

export const fetchAvgOnlineOfflineDetails = createAsyncThunk<Charger[], any>(
  'location-management/fetchAvgOnlineOfflineDetails',
  async (data, { dispatch }) => {
    try {
      const response = await stationManagement.fetchAvgOnlineOffline(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// Initial state
const initialState: StationManagementInitialProperties = {
  selectedChargingPoint: null,
  stationList: [],
  stationListLoading: false,
  chargingStationList: [],
  selectedStationPerformanceList: null,
  selectedLocationPerformanceList: null,
  selectedSingleStationPerformanceList: null,
  selectedSingleLocationPerformanceList: null,
  onlineAvgHours: '0',
  offlineTotalHours: '0',
  activeTabKey: 'liveLogsSession',
};
// Create the slice
export const chargingPointSlice = createSlice({
  name: 'device-management',
  initialState,
  reducers: {
    /**
     * Function to update selectedChargingPoint
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSelectedStation: (state, action) => {
      state.selectedChargingPoint = action.payload;
    },
    /**
     * Function to update selectedChargingPoint
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setStationPerformance: (state, action) => {
      state.selectedStationPerformanceList = action.payload;
      state.selectedLocationPerformanceList = action.payload;
    },
    /**
     * Function to update selectedChargingPoint
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSingleStationPerformance: (state, action) => {
      state.selectedSingleStationPerformanceList = action.payload;
      state.selectedSingleLocationPerformanceList = action.payload;
    },
    /**
     * Function to update activeTabKey
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setActiveTabKey: (state, action) => {
      state.activeTabKey = action.payload;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    // Extra reducer
    builder
      .addCase(fetchChargingStationList.pending, (state) => {
        state.stationListLoading = true;
      })
      .addCase(
        fetchChargingStationList.fulfilled,
        (state, action: PayloadAction<ChargingStationList[]>) => {
          state.chargingStationList = action.payload;
          state.stationListLoading = false;
        },
      )
      .addCase(fetchChargingStationList.rejected, (state) => {
        state.stationListLoading = false;
      });
    builder
      .addCase(fetchAvgOnlineOfflineDetails.pending, (state) => {
        state.stationListLoading = true;
      })
      .addCase(fetchAvgOnlineOfflineDetails.fulfilled, (state, action: PayloadAction<any>) => {
        state.onlineAvgHours = action.payload.onlineAvgHours;
        state.offlineTotalHours = action.payload.totalOfflineHours;
      })
      .addCase(fetchAvgOnlineOfflineDetails.rejected, (state) => {
        state.stationListLoading = false;
      });
  },
});

// Export the actions
export const {
  setSelectedStation,
  setStationPerformance,
  setSingleStationPerformance,
  setActiveTabKey,
} = chargingPointSlice.actions;
// Export the reducer
export default chargingPointSlice.reducer;

export const setSingleLocationPerformance = setSingleStationPerformance;
export const setLocationPerformance = setStationPerformance;
